/**
 * auth/impersonationHandoff.ts — completing "Login As Client" on this origin.
 *
 * ── 🔴 WHY (John, 2026-09-17) ────────────────────────────────────────────────
 * *"When I click the login as client button, it takes me to this control center
 * login page instead of logging me directly into their account."*
 *
 * **A Firebase Auth session is per-ORIGIN.** The admin app is
 * `bestaisolutions.com/admin/`; this app is `app.justtextava.com`. Signing in on
 * one does not sign you in on the other — so `?impersonate=` was being read
 * correctly and `claims.role` was empty, which is a sign-in screen.
 *
 * The admin app mints a one-time TICKET and puts it in this URL's FRAGMENT. We
 * trade it for a real Firebase custom token over a POST.
 *
 * ⚠️ THE FRAGMENT IS READ AND ERASED BEFORE ANYTHING ELSE HAPPENS. A fragment is
 * never sent to a server, so it cannot reach an access log or a Referer header —
 * but it does sit in the address bar and in `history`, so it is stripped with
 * `replaceState` on the very first read. `consumeTicket` is named for that: it
 * is not a getter, it takes the ticket away.
 */
import { signInWithCustomToken } from 'firebase/auth'
import { auth } from '../firebase'

/** The fragment key the admin app writes. */
const FRAGMENT_KEY = 'imp'

/**
 * Read the ticket out of the URL fragment AND remove it. Returns null when
 * there is none. Safe to call on every boot.
 */
export function consumeImpersonationTicket(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const hash = window.location.hash.replace(/^#/, '')
    if (!hash) return null
    const params = new URLSearchParams(hash)
    const ticket = params.get(FRAGMENT_KEY)
    if (!ticket) return null

    // Erase it before we do anything with it, so a failure partway through
    // cannot leave the ticket sitting in the address bar.
    params.delete(FRAGMENT_KEY)
    const rest = params.toString()
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + (rest ? `#${rest}` : ''),
    )
    return ticket
  } catch {
    // A locked-down browser may refuse history access. Never let that stop the
    // page from rendering — the worst case is a sign-in screen, which is what
    // happens today anyway.
    return null
  }
}

export type HandoffResult =
  | { ok: true; clientId: string }
  | { ok: false; error: string }

/**
 * Trade the ticket for a session on THIS origin.
 *
 * ⚠️ IT RETURNS A REASON RATHER THAN THROWING. The caller renders a sign-in
 * screen either way; what it must not do is render it *silently* after the
 * operator explicitly asked to be logged in as someone.
 */
export async function redeemImpersonationTicket(ticket: string): Promise<HandoffResult> {
  try {
    const res = await fetch('/api/impersonation-exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket }),
    })
    const data = (await res.json()) as { ok?: boolean; customToken?: string; clientId?: string; error?: string }
    if (!res.ok || !data.ok || !data.customToken) {
      return { ok: false, error: data.error || 'That link is no longer valid.' }
    }
    await signInWithCustomToken(auth, data.customToken)
    return { ok: true, clientId: data.clientId ?? '' }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not complete sign-in.' }
  }
}
